import { HOURS_PER_WORK_DAY } from './workDays';

/*
 * The figures of a summary row of a contract are bound by one formula: the contract (from
 * its rows) = KTA × installers × work days × 8 h. One of KTA, the installers and the work
 * time can be locked; changing another of them changes the one left free. Locking two would
 * leave nothing to change, so only one is locked at a time.
 */
export type ContractLockField = 'averageHourlyEarnings' | 'installers' | 'time';

export type ContractLockRefusal = 'noValue' | 'onlyOne';

// The lock of a summary row; the locked time is kept in work days
export interface IContractLock {
  field: ContractLockField;
  workDays: number;
}

const FIELDS: ContractLockField[] = [
  'averageHourlyEarnings',
  'installers',
  'time',
];

// The locks are saved with the version as JSON, by summary row (its section)
const lockKey = (section: null | string) => section ?? 'installation';

const parseLocks = (
  contractLocks: null | string | undefined
): Record<string, IContractLock> => {
  if (!contractLocks) {
    return {};
  }

  try {
    const locks = JSON.parse(contractLocks);
    return locks !== null && typeof locks === 'object' ? locks : {};
  } catch {
    return {};
  }
};

// The lock of the summary row of the section, null when nothing (valid) is locked
export const getContractLock = (
  contractLocks: null | string | undefined,
  section: null | string
): IContractLock | null => {
  const lock = parseLocks(contractLocks)[lockKey(section)];
  return lock && FIELDS.includes(lock.field) && Number.isFinite(lock.workDays)
    ? { field: lock.field, workDays: lock.workDays }
    : null;
};

// The locks with that of the summary row of the section set or removed; null when none
export const withContractLock = (
  contractLocks: null | string | undefined,
  section: null | string,
  lock: IContractLock | null
): null | string => {
  const locks = parseLocks(contractLocks);
  if (lock === null) {
    delete locks[lockKey(section)];
  } else {
    locks[lockKey(section)] = lock;
  }

  return Object.keys(locks).length === 0 ? null : JSON.stringify(locks);
};

// Why the field cannot be locked, or null when it can
export const getContractLockRefusal = (
  field: ContractLockField,
  lockedField: ContractLockField | null,
  values: {
    averageHourlyEarnings: number;
    installers: number;
    workDays: number;
  }
): ContractLockRefusal | null => {
  if (lockedField !== null && lockedField !== field) {
    return 'onlyOne';
  }

  return values[field === 'time' ? 'workDays' : field] > 0 ? null : 'noValue';
};

// The KTA that makes the contract with the installers in the work days; 0 when none does
export const getAverageHourlyEarningsFor = (
  contract: number,
  installers: number,
  workDays: number
): number =>
  contract > 0 && installers > 0 && workDays > 0
    ? contract / (installers * workDays * HOURS_PER_WORK_DAY)
    : 0;

// The installers that make the contract with the KTA in the work days; 0 when none do
export const getInstallersFor = (
  contract: number,
  averageHourlyEarnings: number,
  workDays: number
): number =>
  contract > 0 && averageHourlyEarnings > 0 && workDays > 0
    ? contract / (averageHourlyEarnings * workDays * HOURS_PER_WORK_DAY)
    : 0;
